import React from "react";
import RedocTryoutManager from "./redoc-tryoutManager";
import { RedocStandalone } from "redoc";
import { convertObj } from "swagger2openapi";
import { dereferenceLocalRefs } from "./helpers/tryout";
import { withMarkdownFixes } from "./helpers/markdownFixes";
import "./redoc.css";
import PagePreloader from "../pagePreloader/pagePreloader";

// Redoc rebuilds its whole store on every render (RedocStandalone hands it a fresh copy of the
// spec), and a rebuild scrolls the page back to the URL's section. Each try-out update re-rendered
// it, and that scroll then switched the try-out window to another operation. So Redoc only
// re-renders when the spec changes: memoised, with the options kept as one constant.
const MemoizedRedoc = React.memo(RedocStandalone);

const REDOC_OPTIONS = {
  nativeScrollbars: true,
  theme: {
    sidebar: {
      backgroundColor: "transparent",
      textColor: "#ffffff",
      activeTextColor: "#ffffff",
      groupItems: {
        activeBackgroundColor: "rgba(2, 74, 108, 0.8)",
        textTransform: "none",
      },
      level1Items: {
        activeBackgroundColor: "rgba(2, 74, 108, 0.8)",
        textTransform: "none",
      },
    },
    rightPanel: {
      backgroundColor: "transparent",
      textColor: "#ffffff",
    },
    codeBlock: {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
    },
    colors: {
      primary: {
        main: "#81c14b",
        light: "#a8d87a",
        dark: "#5a9133",
        contrastText: "#ffffff",
      },
      success: {
        main: "#81c14b",
        light: "#a8d87a",
        dark: "#5a9133",
        contrastText: "#ffffff",
      },
      warning: {
        main: "#fca130",
        light: "#ffb454",
        dark: "#f08a0c",
        contrastText: "#ffffff",
      },
      error: {
        main: "#f93e3e",
        light: "#ff6b6b",
        dark: "#d32f2f",
        contrastText: "#ffffff",
      },
      // Redoc's light-grey boxes (expanded "Authorizations" scopes, callbacks) take these; on the
      // default near-white grey our white text was unreadable.
      gray: {
        50: "#0e5577",
        100: "#0b4664",
      },
      text: {
        primary: "#ffffff",
        // Make secondary text fully white for improved readability
        secondary: "#ffffff",
        light: "#ffffff",
      },
      border: {
        dark: "rgba(255, 255, 255, 0.3)",
        light: "rgba(255, 255, 255, 0.2)",
      },
      responses: {
        success: {
          color: "#81c14b",
          backgroundColor: "rgba(129, 193, 75, 0.1)",
        },
        error: {
          color: "#f93e3e",
          backgroundColor: "rgba(249, 62, 62, 0.1)",
        },
        redirect: {
          color: "#fca130",
          backgroundColor: "rgba(252, 161, 48, 0.1)",
        },
        info: {
          color: "#61affe",
          backgroundColor: "rgba(97, 175, 254, 0.1)",
        },
      },
      http: {
        get: "#61affe",
        // Grey as on the old portal (the green read as a link/accent colour).
        post: "#999999",
        put: "#fca130",
        options: "#0d5aa7",
        patch: "#50e3c2",
        delete: "#f93e3e",
        basic: "#999",
        link: "#31bbb6",
        head: "#9012fe",
      },
    },
    typography: {
      // Redoc defaults to Roboto/Montserrat, which the portal never loads; the
      // left menu takes the headings font. Our CSS keeps Oswald on the titles.
      fontFamily: '"CF Asty Std", sans-serif',
      headings: {
        fontFamily: '"CF Asty Std", sans-serif',
      },
      fontSize: "14px",
      lineHeight: "1.5",
      code: {
        fontSize: "13px",
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        color: "#ffffff",
      },
    },
  },
  scrollYOffset: 0,
  hideDownloadButton: false,
  disableSearch: false,
  expandResponses: "200,201",
  requiredPropsFirst: true,
  sortPropsAlphabetically: false,
  pathInMiddlePanel: true,
  hideHostname: false,
  noAutoAuth: false,
  showExtensions: true,
  hideSingleRequestSampleTab: false,
  jsonSampleExpandLevel: 2,
  menuToggle: true,
  // Redoc's own loader takes the theme's green; the portal's page loader stays up instead
  // until Redoc reports it has rendered (onLoaded below).
  hideLoading: true,
};

class RedocTryoutHOC extends React.Component {
  state = {
    swaggerJson: null,
    isConverting: false,
    redocLoaded: false,
  };

  // Stable (class field), so the memoised Redoc does not re-render because of it.
  handleRedocLoaded = () => this.setState({ redocLoaded: true });

  componentDidMount() {
    const { swaggerSpec } = this.props;

    // If we have a swagger spec object, convert it to OpenAPI 3.0
    if (swaggerSpec) {
      this.setState({ isConverting: true });
      const options = { patch: true, warnOnly: true, text: "{}" };
      convertObj(swaggerSpec, options, (err, res) => {
        if (err) {
          console.error("Error converting swagger to OpenAPI:", err);
          // Even if conversion fails, try to use the original spec
          this.setState({
            swaggerJson: withMarkdownFixes(swaggerSpec),
            isConverting: false,
            redocLoaded: false,
          });
          return;
        }

        // Use the converted OpenAPI spec
        if (res && res.openapi) {
          this.setState({
            swaggerJson: withMarkdownFixes(res.openapi),
            isConverting: false,
            redocLoaded: false,
          });
        } else {
          // Fallback to original spec if conversion doesn't produce expected result
          this.setState({
            swaggerJson: withMarkdownFixes(swaggerSpec),
            isConverting: false,
            redocLoaded: false,
          });
        }
      });
    }
  }

  componentDidUpdate(prevProps) {
    // If swaggerSpec prop changes, update the state
    if (
      prevProps.swaggerSpec !== this.props.swaggerSpec &&
      this.props.swaggerSpec
    ) {
      this.setState({ isConverting: true });
      const options = { patch: true, warnOnly: true, text: "{}" };
      convertObj(this.props.swaggerSpec, options, (err, res) => {
        if (err) {
          console.error("Error converting swagger to OpenAPI:", err);
          this.setState({
            swaggerJson: withMarkdownFixes(this.props.swaggerSpec),
            isConverting: false,
            redocLoaded: false,
          });
          return;
        }

        if (res && res.openapi) {
          this.setState({
            swaggerJson: withMarkdownFixes(res.openapi),
            isConverting: false,
            redocLoaded: false,
          });
        } else {
          this.setState({
            swaggerJson: withMarkdownFixes(this.props.swaggerSpec),
            isConverting: false,
            redocLoaded: false,
          });
        }
      });
    }
  }

  // Dereferenced copy for the try-out (request body templates need real schemas), memoised per spec.
  getTryoutSpec(swaggerJson) {
    if (this.tryoutSpecSource !== swaggerJson) {
      this.tryoutSpecSource = swaggerJson;
      try {
        this.tryoutSpec = dereferenceLocalRefs(swaggerJson);
      } catch (e) {
        console.error("Could not dereference the spec for the try-out:", e);
        this.tryoutSpec = swaggerJson;
      }
    }
    return this.tryoutSpec;
  }

  render() {
    const { swaggerJson, isConverting, redocLoaded } = this.state;
    const { documentationId } = this.props;

    if (isConverting) {
      return <PagePreloader completed={false} />;
    }

    if (!swaggerJson) {
      return <div>No API specification available</div>;
    }

    return (
      <>
        {!redocLoaded && <PagePreloader completed={false} />}
        <MemoizedRedoc
          spec={swaggerJson}
          options={REDOC_OPTIONS}
          onLoaded={this.handleRedocLoaded}
        />
        <RedocTryoutManager
          {...this.props}
          swaggerJson={this.getTryoutSpec(swaggerJson)}
          documentationId={documentationId}
        />
      </>
    );
  }
}

export default RedocTryoutHOC;
